import React, { forwardRef, memo, useImperativeHandle, useMemo, useRef } from 'react';
import cx from 'classnames';
import { Virtuoso } from 'react-virtuoso';
import { Skeleton, Stack } from '@mui/material';
import PipelineOriginCardContainer from 'js/components/pipelines/cards/PipelineOriginCardContainer';
import SourcePipelineContainer from 'js/components/pipelines/SourcePipelineContainer';
import { useDrawerContext } from 'new/components/drawer/useDrawerContext';
import { CANVAS_HEIGHT } from 'js/components/pipelines/FlowNodes/consts';
import {
  ARCHIVAL_STATUS_ARCHIVED,
  ARCHIVAL_STATUS_RESTORING,
} from 'js/constants/pipelineConstants';
import { Checkbox } from 'new/elements/Checkbox/Checkbox';

import styles from './PipelinesList.module.scss';

const ARCHIVAL_STATUSES_FOR_ARCHIVED_ROW = [ARCHIVAL_STATUS_ARCHIVED, ARCHIVAL_STATUS_RESTORING];

const renderLoading = (index) => (
  <div id={`el${index}`} className="pipeline-root-container">
    <div className="list-layout" style={{ borderBottom: '1px solid var(--color-greyscale-100)' }}>
      <Stack direction="row" spacing="15px" alignItems="center" sx={{ margin: '15px 12px' }}>
        <Skeleton width={34} height={34} />
        <Stack spacing="4px" sx={{ flex: '1' }}>
          <Skeleton width="80%" height={20} />
          <Skeleton width="80%" height={20} />
          <Skeleton width="80%" height={20} />
        </Stack>
        <Skeleton width={86} height={27} />
      </Stack>
    </div>
  </div>
);

const itemContent =
  ({
    onClickExpandFlow,
    _handleSourceClicked,
    selectedDataSource,
    scaleFactor,
    _sourcePipelineElement,
    projectId,
    onRootRef,
    _toggleCloneFlowModal,
    refreshPipeline,
    isOldDesign,
    toggleUseExpanded,
    onItemCheckboxChange,
    isItemCheckboxChecked,
  }) =>
  (index, source) => {
    if (!source) {
      return null;
    }

    const enableCheckbox = typeof onItemCheckboxChange === 'function';
    const isChecked = enableCheckbox && !!isItemCheckboxChecked?.(source?.origin_node_id);

    const isPipelineSelected = selectedDataSource && selectedDataSource === source.flow_node_id;
    // Use a fixed height calculation instead of offsetHeight to prevent overlap issues
    // when multiple flows are stacked. The height accounts for:
    // - CANVAS_HEIGHT (500px)
    // - marginBottom (20px)
    // - toolbar, controls, and padding (60px)
    const pipelineContainerHeight = isPipelineSelected ? CANVAS_HEIGHT : 0;

    // Total height for Virtuoso tracking (header + canvas container)
    const HEADER_HEIGHT = 116; // min-height 100px + padding 8px*2
    const totalItemHeight = isPipelineSelected
      ? HEADER_HEIGHT + pipelineContainerHeight
      : HEADER_HEIGHT;

    return (
      <div
        key={source.flow_node_id || source.origin_node_id}
        id={`el${index}`}
        className={cx(
          'pipeline-root-container',
          isPipelineSelected && 'is-expanded',
          enableCheckbox && styles.containerWithCheckbox,
          isChecked && styles.rowSelected,
          ARCHIVAL_STATUSES_FOR_ARCHIVED_ROW.includes(source?.archival_status) && 'is-archived',
        )}
        data-known-size={totalItemHeight}
        data-item-index={index}
      >
        <Stack width="100%">
          <div data-index={index}>
            <PipelineOriginCardContainer
              leftChildren={
                enableCheckbox ? (
                  <label htmlFor={`checkbox-${source.flow_node_id}`}>
                    <Checkbox
                      subtle
                      type="checkbox"
                      id={`checkbox-${source.flow_node_id}`}
                      checked={isChecked}
                      onChange={() => onItemCheckboxChange(source?.origin_node_id)}
                    />
                  </label>
                ) : null
              }
              isOldDesign={isOldDesign}
              cardData={source}
              projectId={projectId}
              toggleCloneFlowModal={_toggleCloneFlowModal}
              onExpand={_handleSourceClicked}
              isPipelineSelected={isPipelineSelected}
            />
          </div>
          <div className="source-pipeline-container" style={{ height: pipelineContainerHeight }}>
            {isPipelineSelected && (
              <SourcePipelineContainer
                key={source.flow_node_id || source.origin_node_id}
                onClickExpandFlow={onClickExpandFlow}
                refreshPipeline={refreshPipeline}
                onRootRef={onRootRef}
                dataSource={source}
                scaleFactor={scaleFactor}
                toggleUseExpanded={toggleUseExpanded}
                projectId={projectId}
              />
            )}
          </div>
        </Stack>
      </div>
    );
  };

const PipelinesList = forwardRef((props, ref) => {
  const virtuosoRef = useRef(null);
  const { sources, disableVirtualization, isLoading } = props;

  useImperativeHandle(ref, () => ({
    scrollToIndex: (index) => {
      if (virtuosoRef.current) {
        virtuosoRef.current.scrollToIndex(index);
      }
    },
  }));

  const data = useMemo(() => (isLoading ? Array(10).fill(null) : sources), [sources, isLoading]);

  const renderer = useMemo(() => itemContent({ ...props }), [props]);

  const { drawerContentRef } = useDrawerContext();
  // fallback to mainContentRef at the old ui
  const mainContentRef = useMemo(() => document.getElementById('main-content'), []);

  // TODO: remove this after the loading issue is resolved

  if (isLoading) {
    return Array(30)
      .fill(null)
      .map((_, index) => renderLoading(index));
  }

  if (disableVirtualization) {
    return data.map((source, index) => {
      return renderer(index, source);
    });
  }

  return (
    <Virtuoso
      ref={virtuosoRef}
      data={data}
      itemContent={renderer}
      customScrollParent={drawerContentRef.current || mainContentRef || undefined}
      useWindowScroll={!drawerContentRef.current}
      overscan={2000}
    />
  );
});

export default memo(PipelinesList, (old, newProps) => {
  return [
    'sources',
    'projectId',
    'scaleFactor',
    'isRefreshingPipeline',
    '_sourcePipelineElement',
    'selectedDataSource',
    'isLoading',
    'toggleUseExpanded',
    'onItemCheckboxChange',
    'isItemCheckboxChecked',
  ].every((name) => old[name] === newProps[name]);
});
